/**
 * Evidence accounting behind the boot gate's DOWN debounce.
 *
 * A domain that is genuinely OFF stays off — it is a persistent state. A
 * readiness DIP is not: a rollout restart, an OOM kill, a node drain or a
 * liveness blip truthfully reports `nodeReady: false` for 10-60s. Closing the
 * gate on the first such read discards the operator's uncommitted work in the
 * rules table (draft params, sort order, multi-select) — the same teardown
 * `EntryPage` already refuses to do on a transient socket drop.
 *
 * Pure and separate from the component so the rules below are unit-testable on
 * their own: they are timing logic whose bugs are invisible in a render
 * assertion, and a test that drives them through the component ends up
 * validating the constants against themselves.
 */

/** Distinct polls that must report `false` before the gate closes. */
export const DOWN_CONFIRM_READS = 2;

/** Minimum continuous `false` once the read quota is met. */
export const DOWN_CONFIRM_MS = 10000;

/**
 * Ceiling on the whole debounce, ~2.5 poll cycles.
 *
 * Without it the wait is unbounded: the read quota can never be met if no
 * FURTHER SUCCESSFUL read ever lands, which is exactly what happens when the
 * whole control plane goes down at once, or on a mid-session VPN drop —
 * react-query keeps the last `data` and freezes `dataUpdatedAt` while refetches
 * fail, so the one down-read stays the only one forever. Dip protection is
 * unaffected: a 10-60s dip resolves well inside this window.
 */
export const DOWN_CONFIRM_MAX_MS = 75000;

export interface DownEvidence {
  /** Distinct successful reads that reported `false`. */
  reads: number;
  /** `dataUpdatedAt` of the last read already counted. */
  lastCountedRead: number;
  /** When the current uninterrupted run of `false` began. */
  since: number | null;
}

export const NO_DOWN_EVIDENCE: DownEvidence = {
  reads: 0,
  lastCountedRead: 0,
  since: null
};

/**
 * Fold one observation of `nodeReady === false` into the accumulated evidence.
 *
 * A re-render is not evidence: only a NEW `dataUpdatedAt` counts. Without that
 * dedup any extra effect run would inflate the read count and collapse the
 * debounce back to "close on the first down".
 */
export function recordDownRead(
  prev: DownEvidence,
  dataUpdatedAt: number,
  now: number
): DownEvidence {
  if (dataUpdatedAt === prev.lastCountedRead) return prev;
  return {
    reads: prev.reads + 1,
    lastCountedRead: dataUpdatedAt,
    since: prev.since ?? now
  };
}

/**
 * How long the gate must still wait before it may close — `0` means now.
 *
 * `since` is never restarted by a fresh read, so a stream of reads cannot
 * starve the clock.
 */
export function downConfirmDelay(evidence: DownEvidence, now: number): number {
  const elapsed = now - (evidence.since ?? now);
  if (elapsed >= DOWN_CONFIRM_MAX_MS) return 0;
  if (evidence.reads >= DOWN_CONFIRM_READS) {
    return elapsed >= DOWN_CONFIRM_MS ? 0 : DOWN_CONFIRM_MS - elapsed;
  }
  return DOWN_CONFIRM_MAX_MS - elapsed;
}
